import { useEffect, useState } from 'react'
import { InlineAlert } from '../../../components/InlineAlert'
import { createJudgmentGate, getJudgmentGateByDecision } from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import type { DecisionView, JudgmentGateView } from '../../../types/cartridgeStudio'

export interface JudgmentGateBuilderProps {
  decision: DecisionView
}

/**
 * Design doc §4.5 — a `kind=gate` Decision's `JudgmentGate` (max one per Decision, real `409` if
 * one already exists). No `PATCH` endpoint exists on the merged backend — once a gate exists this
 * becomes a read-only display; the UI must not imply editing is supported. A `422` from attempting
 * this under a `kind=table`/`aggregate` Decision is handled defensively even though this component
 * is only ever rendered for `kind=gate` nodes (AC3's explicit failure path — trust the API, not the UI).
 */
export function JudgmentGateBuilder({ decision }: JudgmentGateBuilderProps) {
  const [gate, setGate] = useState<JudgmentGateView | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [prompt, setPrompt] = useState('')
  const [guidance, setGuidance] = useState('')
  const [rubric, setRubric] = useState('{}')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    // See ProgramWorkspaceScreen's effect for why the `controller.signal.aborted` guard is needed
    // (React 18 StrictMode double-invoke race).
    const controller = new AbortController()
    setError(null)
    setGate(undefined)
    getJudgmentGateByDecision(decision.id, controller.signal)
      .then((view) => {
        if (controller.signal.aborted) return
        setGate(view)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (err instanceof ApiError) setError(err.problem.detail ?? err.message)
        else setError('Could not load the judgment gate for this Decision.')
      })
    return () => controller.abort()
  }, [decision.id])

  let parsedRubric: unknown = null
  let rubricJsonError = false
  try {
    parsedRubric = JSON.parse(rubric)
  } catch {
    rubricJsonError = true
  }
  const isValid = prompt.trim() !== '' && guidance.trim() !== '' && !rubricJsonError

  async function handleCreate() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const created = await createJudgmentGate(decision.id, { prompt: prompt.trim(), guidance: guidance.trim(), rubric: parsedRubric })
      setGate(created)
    } catch (err) {
      const violations = err instanceof ApiError ? err.problem.violations : undefined
      setError(
        violations && violations.length > 0
          ? violations.map((v) => v.message ?? v.code).join('; ')
          : err instanceof ApiError
            ? (err.problem.detail ?? err.message)
            : 'Could not create this judgment gate.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (gate === undefined && !error) {
    return <div style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>Loading…</div>
  }

  if (gate) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
        <InlineAlert severity="info">
          This gate is authored and cannot be edited here — no update endpoint exists on the merged backend.
        </InlineAlert>
        <div className="iw-field">
          <span className="iw-field-label">Prompt</span>
          <p style={{ margin: 0 }}>{gate.prompt}</p>
        </div>
        <div className="iw-field">
          <span className="iw-field-label">Guidance</span>
          <p style={{ margin: 0 }}>{gate.guidance}</p>
        </div>
        <div className="iw-field">
          <span className="iw-field-label">Rubric</span>
          <code style={{ fontFamily: 'var(--iw-font-mono)', fontSize: 'var(--iw-size-xs)' }}>{JSON.stringify(gate.rubric)}</code>
        </div>
      </div>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void handleCreate()
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}
    >
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      <div className="iw-field">
        <label className="iw-field-label" htmlFor={`gate-prompt-${decision.id}`}>
          Prompt
        </label>
        <textarea id={`gate-prompt-${decision.id}`} className="iw-input" rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      </div>
      <div className="iw-field">
        <label className="iw-field-label" htmlFor={`gate-guidance-${decision.id}`}>
          Guidance
        </label>
        <textarea id={`gate-guidance-${decision.id}`} className="iw-input" rows={2} value={guidance} onChange={(e) => setGuidance(e.target.value)} />
      </div>
      <div className="iw-field">
        <label className="iw-field-label" htmlFor={`gate-rubric-${decision.id}`}>
          Rubric (JSON)
        </label>
        <textarea
          id={`gate-rubric-${decision.id}`}
          className="iw-input"
          rows={2}
          style={{ fontFamily: 'var(--iw-font-mono)' }}
          value={rubric}
          onChange={(e) => setRubric(e.target.value)}
        />
        {rubricJsonError ? <span style={{ color: 'var(--iw-danger)', fontSize: 'var(--iw-size-xs)' }}>Must be valid JSON.</span> : null}
      </div>
      <button type="submit" className="iw-btn iw-btn--primary" disabled={!isValid || submitting}>
        {submitting ? 'Creating…' : 'Create judgment gate'}
      </button>
    </form>
  )
}
